/* import __COLOCATED_TEMPLATE__ from './bulk-action-controls.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type CommandKService from 'embercom/services/command-k';
import type InboxState from 'embercom/services/inbox-state';
import { type UpdateTicketStateCommandKContext } from 'embercom/objects/inbox/command-k/update-ticket-state';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import type TicketStateService from 'embercom/services/ticket-state-service';
import { perform } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type InboxApi from 'embercom/services/inbox-api';
import PredicateGroup, { type Predicate } from 'embercom/objects/inbox/search/predicate-group';
import { buildUpdateTicketStateCommandKContext } from 'embercom/lib/inbox2/ticket-custom-states';
import type TicketCustomState from 'embercom/objects/inbox/ticket-custom-state';
import { QaStatusValue } from 'embercom/components/inbox2/conversations-table/table-cells/qa-status';
import { request } from 'embercom/lib/inbox/requests';
import { Channel } from 'embercom/models/data/inbox/channels';
import type WorkflowDetails from 'embercom/objects/inbox/workflow-details';

interface QaAdminResponse {
  id: number;
  name: string;
  is_bot?: boolean;
  is_github_bot?: boolean;
  is_operator?: boolean;
  is_facebook_bot?: boolean;
  is_team?: boolean;
}

const QA_REVIEWER_EXCLUDED_ADMIN_IDS = [-1, -2];

function isQaReviewerOption(admin: QaAdminResponse): boolean {
  if (QA_REVIEWER_EXCLUDED_ADMIN_IDS.includes(admin.id)) {
    return false;
  }
  if (admin.is_bot || admin.is_github_bot || admin.is_operator || admin.is_facebook_bot) {
    return false;
  }
  if (admin.is_team) {
    return false;
  }
  return true;
}

export interface BulkActionControlsArgs {
  narrow: boolean;
  conversationIds: number[];
  selectedCount: number;
  totalCount: number;
  onToggleAll: () => void;
  canShowCommandKActions: boolean;
  canBulkAssign: boolean;
  canBulkReopen: boolean;
  canUpdateTicketState: boolean;
  canBulkPrioritise: boolean;
  isLinkedConversationsView?: boolean;

  // todo: these should be bound correctly with args with the table view component
  onBulkAssign: (opts: {}) => void;
  onBulkEdit: () => void;
  onBulkReopen: () => void;
  onBulkClose: () => void;
  onDisplayActions: () => void;
  onUpdateTicketState?: (state: TicketCustomState['id']) => void;
  onTogglePriority?: () => void;
  onBulkTriggerWorkflow?: (workflowDetails: WorkflowDetails) => void;
  onSelectTrackerTicket: (conversation: ConversationTableEntry) => void;

  // Bulk export
  canBulkExport?: boolean;
  onBulkExportText: () => void;
  onBulkExportPdf: () => void;

  // QA mode bulk actions
  isQaMode?: boolean;
  onBulkUpdateQaStatus?: (status: string) => void;
  onBulkUpdateQaReviewer?: (adminId: number) => void;
}

interface Signature {
  Element: HTMLDivElement;
  Blocks: {
    header: [
      {
        selectedCount: number;
        totalCount: number;
        onToggleAll: () => void;
      },
    ];
  };
  Args: BulkActionControlsArgs;
}

export default class BulkActionControls extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare commandK: CommandKService;
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare ticketStateService: TicketStateService;
  @service declare inboxApi: InboxApi;

  @tracked bulkAssignSource?: string;
  @tracked hasTrackerTicket?: boolean;
  @tracked qaReviewerAdmins: QaAdminResponse[] = [];

  constructor(owner: unknown, args: any) {
    super(owner, args);

    perform(this.fetchTrackerTicket);
    if (this.args.isQaMode) {
      perform(this.fetchQaReviewers);
    }
  }

  get updateTicketStateContext(): UpdateTicketStateCommandKContext {
    return buildUpdateTicketStateCommandKContext({
      customStates: this.ticketStateService.liveTicketCustomStates,
      allowClosedState: false,
      intl: this.intl,
    });
  }

  get trackerTicketSearchQuery() {
    let predicates: Predicate[] = [
      {
        identifier: 'ticket-category',
        type: 'string',
        attribute: 'ticket_category',
        comparison: 'eq',
        value: 'tracker',
      },
      {
        identifier: 'ticket-type',
        type: 'integer',
        attribute: 'ticket_type_id',
        comparison: 'known',
        value: null,
      },
    ];

    let predicatesGroup = new PredicateGroup(predicates);

    return {
      query: '',
      sortParams: {
        sort_field: 'sorting_updated_at' as const,
        sort_direction: 'desc' as const,
      },
      count: 1,
      predicates: { predicates: predicatesGroup.predicates },
      fields: [],
    };
  }

  get canBulkTriggerWorkflow() {
    return (
      this.session.workspace.isFeatureEnabled('bulk-trigger-workflow') &&
      this.session.workspace.hasAccessToWorkflows &&
      !!this.args.onBulkTriggerWorkflow
    );
  }

  get bulkTriggerWorkflowContext() {
    return { conversation: { currentChannel: Channel.Unknown } };
  }

  get canBulkLinkToTracker() {
    return this.hasTrackerTicket && !this.args.isLinkedConversationsView;
  }

  // TODO: Replace IcSelect + __separator__ workaround with IcDropdownOpener @groupList
  // once IcDropdownOpener is fixed to close on router transitions.
  // See: https://github.com/intercom/embercom/pull/118243#discussion_r2932827661
  get qaStatusSelectItems() {
    let component = 'inbox2/conversations-table/table-cells/qa-status-select-item';

    return [
      {
        value: QaStatusValue.Unreviewed,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed'),
        component,
        componentAttrs: { iconType: 'dot' },
      },
      {
        value: QaStatusValue.PassedReview,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review'),
        component,
        componentAttrs: { iconType: 'check' },
      },
      {
        value: '__separator__',
        text: '',
        component: 'inbox2/conversations-table/table-cells/qa-status-separator',
        componentShouldReplaceItem: true,
      },
      {
        value: QaStatusValue.NeedsFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix'),
        component,
        componentAttrs: { iconType: 'alert' },
      },
      {
        value: QaStatusValue.WontFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix'),
        component,
        componentAttrs: { iconType: 'check' },
      },
      {
        value: QaStatusValue.FixComplete,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete'),
        component,
        componentAttrs: { iconType: 'check' },
      },
    ];
  }

  get qaReviewerSelectItems() {
    return this.qaReviewerAdmins.map((admin) => ({
      value: admin.id,
      text: admin.name,
    }));
  }

  @action onDisplayBulkAssign(opts: { source: string }) {
    this.bulkAssignSource = opts.source;
    this.commandK.findAndShow('assign-to', () => (this.bulkAssignSource = undefined), opts);
  }

  @task({ restartable: true })
  *fetchTrackerTicket() {
    let { conversations } = yield this.inboxApi.searchForConversationsTableV2(
      this.trackerTicketSearchQuery,
    );
    this.hasTrackerTicket = !!conversations.length;
  }

  @task({ restartable: true })
  *fetchQaReviewers(): TaskGenerator<void> {
    let appId = this.session.workspace.id;
    let response: Response = yield request(`/teammate_app/qa/reviewers?app_id=${appId}`);
    let admins: QaAdminResponse[] = yield response.json();
    this.qaReviewerAdmins = admins.filter(isQaReviewerOption);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Common::BulkActionControls': typeof BulkActionControls;
    'inbox2/common/bulk-action-controls': typeof BulkActionControls;
  }
}
